import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { TextField } from '@material-ui/core';
import { Clear, Search } from '@material-ui/icons';

const S = {
  Wrapper: styled.div`
    position: relative;
  `,
  Search: styled(({ focused, hovered, ...otherProps }) => <Search {...otherProps} />)`
    position: absolute;
    top: 50%;
    left: 0.5rem;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    color: ${({ focused, hovered, theme }) => (focused || hovered ? theme.palette.primary.main : theme.palette.grey[500])};
  `,
  TextField: styled(({ focused, InputProps, ...otherProps }) => (
    <TextField
      InputProps={{
        classes: {
          root: 'input-root',
          input: 'input',
          notchedOutline: 'notchedOutline',
          focused: 'focused',
        },
        ...InputProps,
      }}
      {...otherProps}
    />
  ))`
    .input {
      height: 40px;
      padding: 0 2rem 0 2.5rem;
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    }
    .notchedOutline {
      border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    }
    ${({ focused, theme }) => (focused && css`
      .notchedOutline {
        border: 2px solid ${theme.palette.primary.main};
      }
    `)}
  `,
  Clear: styled(Clear)`
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.palette.grey[600]};
    cursor: pointer;
  `,
};

/**
 * SearchField를 사용하여 목록을 검색할 수 있습니다.
 *
 * @version 1.0.0
 * @author [chayeoi](https://github.com/chayeoi)
 */

const SearchField = ({ value, onClear, ...otherProps }) => {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const input = useRef(null);

  const handleClick = useCallback(() => input.current.focus(), [input]);
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);
  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  return (
    <S.Wrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <S.Search hovered={hovered} focused={focused} onClick={handleClick} />
      <S.TextField
        type="search"
        inputRef={input}
        value={value}
        variant="outlined"
        margin="none"
        fullWidth
        inputProps={{
          onFocus: handleFocus,
          onBlur: handleBlur,
        }}
        focused={focused}
        {...otherProps}
      />
      {value && <S.Clear onClick={onClear} />}
    </S.Wrapper>
  );
};

SearchField.propTypes = {
  /** 입력 필드에 값을 입력하기 전에 사용자에게 제공될 힌트 */
  placeholder: PropTypes.string,
  /** 사용자 입력값 */
  value: PropTypes.string.isRequired,
  /** 입력값 지우기 버튼을 클릭했을 때 호출될 콜백 함수 */
  onClear: PropTypes.func.isRequired,
  /** Input 컴포넌트에 전달될 props 객체 */
  InputProps: PropTypes.shape({}),
};

SearchField.defaultProps = {
  placeholder: '',
  InputProps: {},
};

export default SearchField;
